import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import Swal from 'sweetalert2'

import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload'

import { deepEqual } from 'fast-equals'
import { NewInfrastructureModalBody } from 'pages/costs/modals/NewInfrastructureModalBody'
import { DataTablePFSEvent } from 'primereact/datatable'
import { toast } from 'react-toastify'
import {
	colorConstants,
	ColData,
	HardFilterWrapper,
	HardFilterWrapperHandle,
	MultipleDeleter,
	Table,
	UfinetActionButtonHandle,
	UfinetBox,
	UfinetButton,
	UfinetModal,
	UfinetSectionBox,
} from 'ufinet-web-components'
import {
	AreaType,
	AuthContext,
	Authority,
	defaultPageNumber,
	defaultPageSize,
	EmptyPagingData,
	getTableTranslations,
	ICostsInfrastructureResponse,
	ICostsQueryCriteria,
	infrastructureCostsService,
	infrastructureTypeMappings,
	mapFromPagedResponseToPagingData,
	onSelectionChangePreventDefaultsRemoval,
	PagedResponse,
	PagingData,
	useAsync,
	useModal,
} from 'ufinet-web-functions'
import { colsInfrastructure } from './common/CostsColData'
import { withDownloadNotificationNames } from './common/downloadNotificationIntl'
import { useColumnTextFilter } from './common/tableTextFilter'
import { useTableSort } from './common/useTableSort'
import {
	buildEmptyPagedResponse,
	getCountryIdsFromTableFilter,
	getSelectFilterValues,
	mergeCountryFilters,
	useCostsCountryCatalog,
} from './common/useCostsCountryFilters'
import { UploadExcelModalBody } from './modals/excel/UploadExcelModalBody'

type PagedInfrastructureResponse = PagedResponse<ICostsInfrastructureResponse>

const InfrastructurePage: FC = () => {
	const intl = useIntl()
	const actionButtonRef = useRef<UfinetActionButtonHandle>()
	// Guards against an in-flight, slower search overwriting the results of a newer one (e.g. clicking
	// Buscar right after typing a filter): only the response of the latest request is applied.
	const requestIdRef = useRef(0)
	const [infrastructureResponse, setInfrastructureResponse] = useState<ICostsInfrastructureResponse[]>([])
	const { sortField, sortOrder, handleSort, sortData } = useTableSort<ICostsInfrastructureResponse>()
	const [pagingData, setPagingData] = useState<PagingData>(EmptyPagingData)
	const [pagingState, setPagingState] = useState({ pageNumber: defaultPageNumber, pageSize: defaultPageSize })
	const [modal, showModal, hideModal] = useModal()
	const [newModal, showNewModal, hideNewModal] = useModal()
	const [selectedValues, setSelectedValues] = useState<ICostsInfrastructureResponse[]>()
	const [tableCountryFilterIds, setTableCountryFilterIds] = useState<string[]>([])
	const [tableInfrastructureTypeFilterIds, setTableInfrastructureTypeFilterIds] = useState<string[]>([])

	const [loadingRecords, setLoadingRecords] = useState<boolean>(false)

	const authData = useContext(AuthContext)
	const { countryFilterOptions } = useCostsCountryCatalog(authData)
	const roles = authData.userData?.authorities || []
	const permissions = Authority.getCostsPermissions(roles)

	const _infrastructureCostsService = useMemo(() => infrastructureCostsService(authData, intl), [authData, intl])

	// Free-text Cliente/Área filters: see tableTextFilter.tsx for why (broken built-in select match mode).
	const clientNameFilter = useColumnTextFilter('COST.CLIENT')
	const areaNameFilter = useColumnTextFilter('COST.AREA')
	const updatedByFilter = useColumnTextFilter('COST.UPDATEDBY')

	const cols: ColData[] = useMemo(() => {
		// Raw enum values as filter value, translated text only for display - the backend compares against
		// the stored enum value (see InfrastructureType), not the translated label.
		const infrastructureTypeOptions = [
			{ label: intl.formatMessage({ id: 'AERIAL' }), value: 'AERIAL' },
			{ label: intl.formatMessage({ id: 'UNDERGROUND' }), value: 'UNDERGROUND' },
		]
		return colsInfrastructure.map((column) => {
			if (column.field === 'countryName') return { ...column, filterOptions: countryFilterOptions }
			if (column.field === 'clientId') return clientNameFilter.applyToColumn(column)
			if (column.field === 'networkAreaName') return areaNameFilter.applyToColumn(column)
			if (column.field === 'lastModifiedBy') return updatedByFilter.applyToColumn(column)
			if (column.field === 'infrastructureType')
				return { ...column, filterType: 'select', filterOptions: infrastructureTypeOptions }
			return column
		})
	}, [
		countryFilterOptions,
		intl,
		clientNameFilter.applyToColumn,
		areaNameFilter.applyToColumn,
		updatedByFilter.applyToColumn,
	])

	const upperFilterRef = useRef<HardFilterWrapperHandle>(null)

	// Reset to page 1 whenever the free-text Cliente/Área/Actualizado por search term changes, so results
	// from the new search aren't hidden behind a page number that no longer makes sense.
	useEffect(() => {
		setPagingState((prev) => ({ ...prev, pageNumber: defaultPageNumber }))
	}, [clientNameFilter.value, areaNameFilter.value, updatedByFilter.value])

	const getInfrastructureCosts = useCallback(async (): Promise<PagedInfrastructureResponse | null> => {
		const requestId = ++requestIdRef.current
		setLoadingRecords(true)
		actionButtonRef.current?.changeActionStatus(true)
		const hardFilterCountryIds = upperFilterRef.current?.getCountries().map((e) => e.value) || []
		const { countries, hasImpossibleCountryFilter } = mergeCountryFilters(hardFilterCountryIds, tableCountryFilterIds)
		if (hasImpossibleCountryFilter) {
			return buildEmptyPagedResponse<ICostsInfrastructureResponse>(pagingState.pageNumber, pagingState.pageSize)
		}

		// Cliente/Área are searched server-side (LIKE '%term%', see aqs-backend's
		// clientNameSearch/networkAreaNameSearch) so results cover the whole dataset, not just this page.
		const criteria: ICostsQueryCriteria & {
			pageNumber: number
			pageSize: number
			clientNameSearch?: string
			networkAreaNameSearch?: string
			infrastructureTypes?: string[]
			updatedBy?: string
		} = {
			countries,
			networkAreas: upperFilterRef.current?.getAreas().map((e) => e.value) || [],
			pageNumber: pagingState.pageNumber,
			pageSize: pagingState.pageSize,
			clientNameSearch: clientNameFilter.value,
			networkAreaNameSearch: areaNameFilter.value,
			infrastructureTypes: tableInfrastructureTypeFilterIds,
			updatedBy: updatedByFilter.value,
		}

		const result = (await _infrastructureCostsService.findByCriteria(
			criteria
		)) as unknown as PagedInfrastructureResponse
		return requestId === requestIdRef.current ? result : null
	}, [
		_infrastructureCostsService,
		pagingState.pageNumber,
		pagingState.pageSize,
		tableCountryFilterIds,
		tableInfrastructureTypeFilterIds,
		clientNameFilter.value,
		areaNameFilter.value,
		updatedByFilter.value,
	])

	const onInfrastructureCostsFetched = (response: PagedInfrastructureResponse | null): void => {
		if (!response) return // superseded by a newer search; ignore this stale response
		const items = Array.isArray(response) ? (response as unknown as ICostsInfrastructureResponse[]) : response.data
		const parsedCosts = items.map((cost) => ({
			...cost,
			networkAreaName: getTableTranslations(cost.networkAreaName, intl),
			infrastructureType: intl.formatMessage({ id: cost.infrastructureType }),
		}))

		if (!Array.isArray(response)) {
			setPagingData(mapFromPagedResponseToPagingData(response))
		}
		setInfrastructureResponse(parsedCosts)
	}

	const onInfrastructureCostsFailedToFetch = (): void => {
		toast.error(intl.formatMessage({ id: 'COSTS.FETCH.ERROR' }))
	}

	const runFinally = () => {
		setLoadingRecords(false)
		actionButtonRef.current?.changeActionStatus(false)
	}

	// Collapse every filter/paging input into one string signature and drive the fetch off a single
	// incrementing token, so one user action = exactly one /find call. useAsync's internal effect compares
	// its deps per-element; passing the raw filter arrays made it re-fire on every new array reference. A
	// primitive token gated by the signature ignores that reference churn.
	const [searchToken, setSearchToken] = useState(0)
	const criteriaSignature = useMemo(
		() =>
			JSON.stringify([
				pagingState.pageNumber,
				pagingState.pageSize,
				tableCountryFilterIds,
				tableInfrastructureTypeFilterIds,
				clientNameFilter.value,
				areaNameFilter.value,
				updatedByFilter.value,
			]),
		[
			pagingState.pageNumber,
			pagingState.pageSize,
			tableCountryFilterIds,
			tableInfrastructureTypeFilterIds,
			clientNameFilter.value,
			areaNameFilter.value,
			updatedByFilter.value,
		]
	)
	useEffect(() => {
		setSearchToken((token) => token + 1)
	}, [criteriaSignature])

	const fetchAndStore = useAsync(
		{
			asyncFn: getInfrastructureCosts,
			onSuccess: onInfrastructureCostsFetched,
			onFailure: onInfrastructureCostsFailedToFetch,
			runFinally: runFinally,
			// skipMount: the initial load is triggered once by the signature effect below (token 0 -> 1),
			// so we avoid a duplicate fetch on first render.
			options: { skipMount: true },
		},
		[searchToken]
	)

	const handlePage = (event: DataTablePFSEvent) => {
		setPagingState({ pageNumber: event.page ?? defaultPageNumber, pageSize: event.rows ?? defaultPageSize })
	}
	const handleFilter = (event: DataTablePFSEvent) => {
		const countryIds = getCountryIdsFromTableFilter(event)
		const infrastructureTypeIds = getSelectFilterValues(event, 'infrastructureType')
		setPagingState((prev) => ({ ...prev, pageNumber: defaultPageNumber }))
		setTableCountryFilterIds(countryIds)
		setTableInfrastructureTypeFilterIds(infrastructureTypeIds)
	}

	/// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
	/// /////////////////////////////////////// HEADER TABLE AND ACTION BUTTONS //////////////////////////////////////////
	/// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

	const getActionButtons = () => {
		return (
			<>
				<OverlayTrigger
					placement="bottom"
					delay={{ show: 250, hide: 400 }}
					overlay={<Tooltip className="tooltip-dark">{intl.formatMessage({ id: 'DOWNLOAD_EXCEL' })}</Tooltip>}
				>
					<div className="d-inline">
						<FontAwesomeIcon
							className="fa-icon-primary"
							size="1x"
							icon={faDownload}
							onClick={() => {
								const hardFilterCountryIds = upperFilterRef.current?.getCountries().map((e) => e.value) || []
								const { countries, hasImpossibleCountryFilter } = mergeCountryFilters(
									hardFilterCountryIds,
									tableCountryFilterIds
								)
								if (hasImpossibleCountryFilter) return
								const areaOptions = upperFilterRef.current?.getAreas() || []
								const idToName = new Map([
									...(countryFilterOptions?.map((o): [string, string] => [o.value, o.label]) || []),
									...areaOptions.map((o): [string, string] => [o.value, o.label]),
								])
								infrastructureCostsService(authData, withDownloadNotificationNames(intl, idToName)).downloadXLS({
									countries,
									networkAreas: areaOptions.map((e) => e.value),
								})
							}}
						/>
					</div>
				</OverlayTrigger>
			</>
		)
	}

	const getHeaderButtons = () => (
		<>
			{permissions.canDelete && (
				<MultipleDeleter
					setSelectedValues={setSelectedValues}
					selectedValues={selectedValues}
					deleteEndpoint={_infrastructureCostsService.deleteByIdsList}
					search={fetchAndStore}
				/>
			)}
			{permissions.canWrite && (
				<>
					<UfinetButton
						className="me-3"
						icon={faPlus}
						onClick={() => showNewModal()}
						content={intl.formatMessage({ id: 'NEW.REGISTER' })}
					/>
					<UfinetButton icon={faUpload} onClick={() => showModal()} content={intl.formatMessage({ id: 'BULK_LOAD' })} />
				</>
			)}
		</>
	)

	const onRowEditComplete = (data: ICostsInfrastructureResponse) => {
		if (
			deepEqual(
				data,
				infrastructureResponse.find((it) => it.id === data.id)
			)
		)
			return

		Swal.fire({
			icon: 'warning',
			title: intl.formatMessage(
				{ id: 'COUNTRY.REGISTRY.UPDATE.TYPE' },
				{ countryName: data.countryName, infrastructureType: data.infrastructureType }
			),
			html: intl.formatMessage({ id: 'COUNTRY.REGISTRY.UPDATE.CONFIRM' }, { networkAreaName: data.networkAreaName }),
			showCancelButton: true,
			showConfirmButton: true,
			reverseButtons: true,
			confirmButtonColor: colorConstants.primaryColor,
			cancelButtonColor: colorConstants.dangerColor,
			preConfirm: () =>
				_infrastructureCostsService
					.update(data.id, {
						countryName: data.countryName,
						networkAreaName: data.networkAreaName,
						client: data.clientId,
						infrastructureType: infrastructureTypeMappings.get(data.infrastructureType) || data.infrastructureType,
						monthlyCostPerMeter: data.monthlyCostPerMeter,
						punctualCost: data.punctualCost,
					})
					.then(fetchAndStore),
		})
	}

	const onSelectionChange = (values: ICostsInfrastructureResponse[]) => {
		onSelectionChangePreventDefaultsRemoval(
			values,
			selectedValues,
			infrastructureResponse,
			setSelectedValues,
			intl.formatMessage({ id: 'BY_DEFAULT.DELETE.WARNING' })
		)
	}

	return (
		<UfinetBox>
			<HardFilterWrapper
				ref={upperFilterRef}
				actionButtonRef={actionButtonRef}
				search={fetchAndStore}
				networkAreaType={AreaType.INFRASTRUCTURE_AREA}
				actionButtons={getActionButtons()}
			/>
			<UfinetSectionBox>
				<Table
					selectedValues={selectedValues}
					onSelectionChange={permissions.canDelete ? onSelectionChange : undefined}
					onRowEditComplete={permissions.canUpdate ? onRowEditComplete : undefined}
					cols={cols}
					content={sortData(infrastructureResponse)}
					sortField={sortField}
					sortOrder={sortOrder}
					headerButtons={getHeaderButtons()}
					onFilterClear={() => {
						clientNameFilter.reset()
						areaNameFilter.reset()
						updatedByFilter.reset()
					}}
					emptyMessage={loadingRecords ? intl.formatMessage({ id: 'LOADING_DOTS' }) : undefined}
					lazySettings={{
						...pagingData,
						loading: loadingRecords,
						onPage: handlePage,
						onSort: handleSort,
						onFilter: handleFilter,
					}}
				/>
			</UfinetSectionBox>
			{/* Modals */}
			<UfinetModal
				size="xl"
				show={modal}
				handleClose={hideModal}
				title={intl.formatMessage({ id: 'MODAL.UPLOAD_EXCEL.TITLE' })}
			>
				<UploadExcelModalBody
					xlsEmptyDownloadCallback={_infrastructureCostsService.downloadEmptyListXLS}
					xlsUploadCallback={_infrastructureCostsService.uploadXLS}
					onXlsUploaded={fetchAndStore}
					hideModal={hideModal}
				/>
			</UfinetModal>
			<UfinetModal
				size="lg"
				show={newModal}
				handleClose={hideNewModal}
				title={intl.formatMessage({ id: 'MODAL.NEW.REGISTER.TITLE' })}
			>
				<NewInfrastructureModalBody findCosts={fetchAndStore} hideModal={hideNewModal} />
			</UfinetModal>
		</UfinetBox>
	)
}

export { InfrastructurePage }
